import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "gourmeto.theme";
const ThemeContext = createContext(null);

function readPreference() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    // ignore
  }
  return null;
}

function useIsNarrow(max = 1023) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= max,
  );

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= max);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [max]);

  return narrow;
}

function autoDarkForPath(path, isNarrow) {
  const isPool = path === "/pool" || path.startsWith("/pool/");
  const darkRoute =
    isPool ||
    path === "/" ||
    path === "/homePage" ||
    path === "/favourites" ||
    path === "/map" ||
    path === "/profile";
  if (isPool) return true;
  return darkRoute && isNarrow;
}

export const ThemeProvider = ({ children }) => {
  const { pathname } = useLocation();
  const isNarrow = useIsNarrow();
  const [preference, setPreference] = useState(readPreference);

  const isDark =
    preference === "dark" ||
    (preference == null && autoDarkForPath(pathname, isNarrow));

  const toggleTheme = () => {
    const next = isDark ? "light" : "dark";
    setPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("theme-dark", isDark);
    root.classList.toggle("theme-light", !isDark);
    document.body.classList.toggle("theme-dark", isDark);
    document.body.classList.toggle("theme-light", !isDark);
    document.body.style.background = isDark ? "#171717" : "#faf7f1";
    document.body.style.color = isDark ? "#f7f3ec" : "#0f0f10";
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, preference, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
};
